import axios from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ApiTaskType } from "src/api/types";
import useLimits from "src/hooks/api/services/auth/useLimits";
import usePatch from "src/hooks/rest/usePatch";
import usePost from "src/hooks/rest/usePost";
import useAuth from "src/hooks/selectors/useAuth";
import useAlert from "src/hooks/useAlert";
import { uploadFile } from "src/lib/uploadFile";
import {
	removeTask,
	TaskFormStatus,
	updateStatusTask,
	updateTask,
} from "src/redux/taskForm";

type SetType = "fill" | "manual" | "merge";

interface GetParams {
	skipLoading?: boolean;
}

type SaveOptions = {
	data?: any;
	forceSave?: boolean;
	disableAlert?: boolean;
};

function useTaskForm() {
	const { t } = useTranslation("tasks", {
		keyPrefix: "alert",
	});
	const limits = useLimits();
	const { id: _id } = useParams() as any;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const auth = useAuth();
	const task = useSelector((state: any) => state.taskForm.task);
	const questions = useSelector((state: any) => state.taskForm.questions);
	const changes = useSelector((state: any) => state.taskForm.changes);
	const current = useSelector((state: any) => state.taskForm.current);
	const status = useSelector((state: any) => state.taskForm.status);
	const [sendAlert] = useAlert();
	const [apiCreate] = usePost("/company/tasks");
	const [apiQuickCreate] = usePost("/company/tasks/quick");
	const id = _id || task?.hid;
	const q = `with[]=answers&with[]=description_obj&with[]=assignees&with[]=teams`;
	const [apiUpdate] = usePatch(id ? `/company/tasks/${id}?${q}` : undefined);

	const get = async (params?: GetParams) => {
		if (status === "loading") {
			return;
		}
		if (!id) return;
		if (!params?.skipLoading) {
			setStatus("loading");
		}
		try {
			const {
				data: { data: _task },
			} = await axios.get(`/company/tasks/${id}?${q}`);

			if (!_task?.address?.street) {
				_task.is_home = true;
			}

			dispatch(
				updateTask({
					task: _task,
					status: "idle",
				})
			);
			return _task;
		} catch (error) {
			setStatus("error");
		}
	};

	const save = async (options?: SaveOptions) => {
		if (!id) return;
		const update = options?.data || (await parseData(changes));
		if (!Object.keys(update)?.length && !options?.forceSave) {
			return;
		}
		setStatus("updating");
		try {
			const { data: _task } = await apiUpdate(update);
			if (!options?.disableAlert) {
				sendAlert({
					type: "success",
					title: t("success.title"),
					text: t("success.text"),
				});
			}

			if (update.type === "PUBSLISHED") {
				limits.actions.get("");
			}

			dispatch(
				updateTask({
					task: {
						...task,
						..._task,
					},
					changes: {},
					status: "idle",
				})
			);
			return _task;
		} catch (error) {
			dispatch(
				updateTask({
					changes: {},
					status: "error",
				})
			);
		}
	};

	const updateCurrent = (n: number) => {
		if (n < 0) return;
		window.scrollTo(0, 0);

		if (task.id && Object.keys(changes).length) {
			save({
				data: changes,
				disableAlert: true,
			});
		}

		dispatch(
			updateTask({
				current: n,
			})
		);
	};

	const setStatus = (status: TaskFormStatus) => {
		dispatch(updateStatusTask(status));
	};

	const set = (data?: any, type: SetType = "manual") => {
		if (data.amount_of_students) {
			data.amount_of_students = Number(data.amount_of_students);
		}
		let update = {
			task: {
				...task,
				...data,
			},
		} as any;
		if (id && type === "manual") {
			update.changes = {
				...(changes || {}),
				...data,
			};
		}
		if (type === "fill") {
			dispatch(
				updateTask({
					task: {
						...data,
					},
				})
			);
			return;
		}

		if (type === "merge") {
			dispatch(
				updateTask({
					task: {
						...task,
						...data,
					},
				})
			);
			return;
		}

		dispatch(updateTask(update));
	};

	const parseData = async (_data: any) => {
		const address = _data.address;
		delete _data.address;
		let data = {
			..._data,
			...(address || {}),
		} as any;

		if (data.is_office) {
			data = {
				...data,
				...auth?.company.address,
			};
		}

		if (data.media) {
			let uploaded_media = [];
			for (let file of data.media) {
				if (file.id) {
					uploaded_media.push(file.id);
				} else {
					let fd = new FormData();
					fd.append("file", file);
					let res = await uploadFile("/media", fd);
					uploaded_media.push(res?.data?.data?.id || null);
				}
			}
			data.media = uploaded_media;
		}

		if (data.start_date) {
			data.start_date = moment(data.start_date).format(
				"YYYY-MM-DD HH:mm:ss"
			);
		}

		if (data.end_date) {
			data.end_date = moment(data.end_date).format("YYYY-MM-DD HH:mm:ss");
		}

		return data;
	};

	const create = async (
		type: ApiTaskType,
		options?: {
			disableRedirect?: boolean;
			updateState?: boolean;
			data?: any;
		}
	) => {
		if (id) return;
		set({
			type,
		});
		setStatus("creating");
		const data = await parseData({
			...task,
			...(options?.data || {}),
			type,
		});
		if (!data?.address?.street) {
			data.is_home = true;
		}
		data.type = type;
		apiCreate(data)
			.then(({ data }) => {
				limits.actions.get("");
				if (!options?.disableRedirect) {
					navigate(`/tasks/create/completed/thanks?hid=${data?.hid}`);
				}
				if (options?.updateState) {
					dispatch(
						updateTask({
							task: data,
							status: "idle",
						})
					);
				}
			})
			.catch(() => {
				sendAlert({
					type: "error",
					title: t("error.title"),
					text: t("error.text"),
				});
				setStatus("idle");
			});
	};

	const createByTitle = async (title: string) => {
		if (id) return;
		setStatus("creating");
		const data = await parseData({
			title,
			description: "",
			start_date: moment().toDate(),
			type: "INTERNAL",
		});
		return apiQuickCreate(data)
			.then(({ data }) => {
				set(data);
				// clear();
				navigate(`/tasks/${data?.hid}/publish`);
				// navigateModal(`/tasks/${data?.hid}/create`);
			})
			.catch(() => {
				setStatus("idle");
			});
	};

	const publish = async () => {
		if (!id) return;
		setStatus("creating");
		try {
			const data: any = {
				type: "PUBLISHED",
				students: task?.students,
				invite_all: task?.invite_all,
				student_hid: task?.student_hid,
				advertise_task: task?.advertise_task,
			};

			if (!task.is_home && !task?.address?.street) {
				data.is_home = 1;
			}

			const { data: _task } = await apiUpdate(data);
			limits.actions.get("");
			dispatch(
				updateTask({
					task: _task,
					status: "idle",
				})
			);
		} catch (error) {
			setStatus("error");
		}
	};

	const archive = async (id: number) => {
		setStatus("deleting");
		try {
			await axios.delete(`/company/tasks/${id}`);
		} catch (error) {
			setStatus("error");
		}
	};

	const clear = () => dispatch(removeTask());

	const getQuestions = async () => {
		if (!id) return;
		const { data } = await axios.get(`/company/tasks/ai/${id}/questions`);

		dispatch(
			updateTask({
				questions: data?.data,
				task: {
					...task,
					answers: (data?.data || []).map((question: any) => ({
						question: question?.question,
						answer: "",
					})),
				},
			})
		);
		return data;
	};

	const setQuestions = (questions: any[]) => {
		dispatch(
			updateTask({
				questions: questions,
			})
		);
	};

	const clearQuestions = () => {
		dispatch(updateTask({ questions: [] }));
	};

	const duplicate = async (id: number) => {
		setStatus("duplicating");
		return axios
			.post(`/company/tasks/${id}/duplicate`)
			.then(({ data }) => {
				// set(data);
				// clear();
				// navigate(`/tasks/${data?.hid}/publish`);
				// navigateModal(`/tasks/${data?.hid}/create`);
				setStatus("idle");
			})
			.catch(() => {
				setStatus("idle");
			});
	};

	return {
		task,
		questions,
		changes,
		status,
		current,
		actions: {
			set,
			clear,
			create,
			updateCurrent,
			setStatus,
			get,
			save,
			createByTitle,
			getQuestions,
			clearQuestions,
			questions,
			setQuestions,
			publish,
			duplicate,
			archive,
		},
	};
}

export default useTaskForm;
